import type { NavItem } from '@shared/models/nav-item.model';

/**
 * The navigation tree.
 *
 * A constant, filtered by the shell at render time against what the signed-in
 * user holds. Each row says what it needs - a `feature`, or a narrower
 * `capability` for a child - and the shell applies one rule to all of them.
 * Nothing here is matched by title any more, which is what the old
 * `SG_IVS_NAV_TITLE` export existed for.
 *
 * Rows are grouped by where they sit in a job's life - Pipeline, Transactions,
 * Reporting & close, Platform - as the "IVS inside the lifecycle" mockup draws
 * it. `section` on a row is its group, and the nav prints a heading wherever
 * the section changes, so a section whose rows are all filtered away leaves no
 * orphan heading. Stages with no screen yet (Quality Control, Delivery,
 * Accounts) are left out rather than shown empty.
 *
 * The record is the assignment, and the work around it lives on its tabs. The
 * cross-record queues (every task assigned to you, every file across your
 * jobs) stay as children of Assignments, because "what is on my plate today"
 * is a different question from "what is on this job".
 *
 * Switching a row off is a feature flag, not a comment: `feature` rows follow
 * their feature's flag, and `flag` names one for a row that is not a feature.
 * The flags and their defaults are in `@core/access/flags`.
 */

export const NAV_DATA: NavItem[] = [
    {
        title: 'Home',
        tooltip: 'Welcome to the Valuation & Advisory portal',
        iconImage: 'home',
        route: '/home'
    },

    /* -- pipeline: from first enquiry to an engaged job --------------------- */
    {
        section: 'Pipeline',
        title: 'Pipeline board',
        tooltip: 'Every job by lifecycle stage - opportunity, engaged, valuation, review, released',
        iconImage: 'view_week',
        route: '/pipeline',
        feature: 'assignments',
        flag: 'pipelineBoard'
    },
    {
        section: 'Pipeline',
        title: 'CRM',
        tooltip: 'Clients and their contacts, with relationship and pipeline analytics',
        iconImage: 'group',
        route: '/crm/companies',
        feature: 'crm',
        children: [
          { title: 'Client Intelligence', iconImage: 'insights', route: '/crm/intelligence', tooltip: 'Relationship, pipeline and engagement analytics across your clients', flag: 'clientIntelligence' },
          { title: 'Companies & contacts', iconImage: 'domain', route: '/crm/companies', tooltip: 'One directory for clients, tenants, lenders and counsel, and the people at them' }
        ]
    },
    {
        section: 'Pipeline',
        title: 'Opportunities',
        tooltip: 'Every bid, from inbound email to won engagement',
        iconImage: 'filter_alt',
        route: '/opportunities',
        capability: 'opportunities.view',
        children: [
          { title: 'All opportunities', iconImage: 'list', route: '/opportunities', tooltip: 'The bids board, in eight stages' },
          { title: 'Add new opportunity', iconImage: 'add_circle', route: '/opportunities/new', tooltip: 'Start an opportunity and tag its client, contacts and people', capability: 'opportunities.edit' }
        ]
    },

    /* -- transactions: the engaged jobs and the work itself ----------------- */
    {
        section: 'Transactions',
        title: 'Projects',
        tooltip: 'Confirmed engagements, created when an opportunity is won',
        iconImage: 'folder_open',
        route: '/projects',
        capability: 'opportunities.view',
        flag: 'projects'
    },
    {
        section: 'Transactions',
        title: 'Today',
        tooltip: 'What needs you today - drafted values to approve, and the requests that need judgement',
        iconImage: 'wb_sunny',
        route: '/assignments/today',
        capability: 'assignments.assist'
    },
    {
        section: 'Transactions',
        title: 'Assignments',
        tooltip: 'Your valuation assignments - the record, its tasks, files and discussion',
        iconImage: 'reorder',
        route: '/assignments',
        feature: 'assignments',
        flag: 'assignmentsNav',
        children: [
          { title: 'All assignments', iconImage: 'list_alt', route: '/assignments', tooltip: 'Search and filter every assignment you can see' },
          { title: 'New assignment', iconImage: 'note_add', route: '/assignments/new', tooltip: 'Create an assignment', capability: 'assignments.create' },
          { title: 'My tasks', iconImage: 'task_alt', route: '/assignments/tasks', tooltip: 'Every task assigned to you, across all of your assignments' },
          { title: 'My files', iconImage: 'folder', route: '/assignments/files', tooltip: 'Every file across your assignments' },
          { title: 'Master data', iconImage: 'rule_folder', route: '/assignments/admin/master-data', tooltip: 'Maintain streets, projects, valuers and the approval queue', capability: 'assignments.masterData' }
        ]
    },
    {
        section: 'Transactions',
        title: 'Batches',
        tooltip: 'Upload a client portfolio and draft every unit, with an approval gate',
        iconImage: 'dynamic_feed',
        route: '/assignments/batches',
        capability: 'assignments.bulk'
    },
    {
        section: 'Transactions',
        title: 'Ask ✦',
        tooltip: 'Ask about the market, the queue or a property - answered from the records, with sources',
        iconImage: 'auto_awesome',
        route: '/assignments/ask',
        capability: 'assignments.assist'
    },
    {
        section: 'Transactions',
        title: 'Map & comparables',
        tooltip: 'Search properties, comparables and assignments on the map',
        iconImage: 'map',
        route: '/map',
        feature: 'map'
    },

    /* -- reporting & close. Quality Control, Delivery and Accounts join this
          section once they have screens. ----------------------------------- */
    {
        section: 'Reporting & close',
        title: 'Report Studio',
        tooltip: 'Author governed templates and write assignment reports',
        iconImage: 'description',
        route: '/report-studio/writer',
        feature: 'reportStudio',
        children: [
          { title: 'Report Writer', iconImage: 'edit_note', route: '/report-studio/writer', tooltip: 'Draft, edit and finalize an assignment report' },
          { title: 'Template Builder', iconImage: 'dashboard_customize', route: '/report-studio/builder', tooltip: 'Author reusable, governed report templates', capability: 'reportStudio.authorTemplates' }
        ]
    },

    /* -- platform ------------------------------------------------------------ */
    {
        section: 'Platform',
        title: 'Agents',
        tooltip: 'Add an agent built by another team, or build and publish your own',
        iconImage: 'smart_toy',
        route: '/agents',
        feature: 'agents',
        children: [
          { title: 'Marketplace', iconImage: 'storefront', route: '/agents', tooltip: 'Browse agents published across the firm' },
          { title: 'Build an Agent', iconImage: 'construction', route: '/agents/build', tooltip: 'Create an agent and publish it to your team', capability: 'agents.build' }
        ]
    },
    {
        section: 'Platform',
        title: 'People',
        tooltip: 'The firm’s own staff - capacity, draw, licence and eligibility',
        iconImage: 'badge',
        route: '/people',
        feature: 'crm'
    },
    {
        section: 'Platform',
        title: 'Data Vault',
        tooltip: 'Browse, filter and export vault records',
        iconImage: 'storage',
        route: '/vault',
        feature: 'vault'
    },
    {
        section: 'Platform',
        title: 'Connectors',
        tooltip: 'Data and system connectors',
        iconImage: 'cable',
        route: '/home/argus',
        feature: 'connectors',
        children: [
          { title: 'Argus', iconImage: 'view_module', route: '/home/argus', tooltip: 'Click to view Argus info' }
        ]
    },
    {
        section: 'Platform',
        title: 'FAQ',
        tooltip: 'Frequently asked questions',
        iconImage: 'help',
        route: '/faq',
        feature: 'faq'
    },
    {
        section: 'Platform',
        title: 'Prompt library',
        tooltip: 'Saved prompts for the assistant',
        iconImage: 'menu_book',
        route: '/prompt',
        flag: 'promptLibrary'
    },
    {
        section: 'Platform',
        title: 'Settings',
        tooltip: 'Feature flags, formats, role defaults and rules, by Global, region and country',
        iconImage: 'settings',
        route: '/settings/feature-flags',
        capability: 'platform.settings'
    },
    /*
     * Ratings is off by default - its `ratings` flag ships false, which is what
     * this entry being a commented-out block used to mean. Switching the flag
     * on shows it only to users provisioned with the Ratings role, who by
     * design see nothing else.
     */
    {
        section: 'Ratings',
        title: 'Ratings',
        tooltip: 'Click to view Ratings',
        iconImage: 'grading',
        route: '/ratings/liability',
        feature: 'ratings',
        children: [
          { title: 'Liability', iconImage: 'gavel', route: '/ratings/liability', tooltip: 'Click to view liability' },
          { title: 'Allowance', iconImage: 'account_balance_wallet', route: '/ratings/allowance', tooltip: 'Click to view Allowance' },
          { title: 'Allowance Status', iconImage: 'rule', route: '/ratings/allowanceStatus', tooltip: 'Click to view Allowance Status' },
          { title: 'Appeal Savings', iconImage: 'savings', route: '/ratings/appealSavings', tooltip: 'Click to view Appeal Savings' },
          { title: 'Appeal Status', iconImage: 'how_to_vote', route: '/ratings/appealStatus', tooltip: 'Click to view Appeal Status' },
          { title: 'Property Search', iconImage: 'travel_explore', route: '/ratings/propertySearch', tooltip: 'Click to view Property Search' }
        ]
    }
  ];
